"use client";

import { useState, useRef, useEffect } from "react";
import { useRouter } from "next/navigation";
import { OmnibusManager } from "@/lib/OmnibusManager.client";
import {
  ChevronDown,
  ChevronRight,
  User,
  LogOut,
  Lock,
  Eye,
  Users,
  Shield,
  Check,
  Edit3,
} from "lucide-react";
import Notification from "@/components/Notification";
import { useImpersonation } from "@/hooks/useImpersonation";
import {
  ImpersonationRole,
  ImpersonationMemberType,
} from "@/lib/auth/ImpersonationTypes";

interface UserMenuProps {
  userEmail: string;
  className?: string;
  tenantId?: string;
  isActuallyAdmin?: boolean;
  memberType?: "owner" | "board-member" | "renter" | "property-manager" | "portal-admin" | null;
}

export default function UserMenu({
  userEmail,
  className = "",
  tenantId,
  isActuallyAdmin = false,
  memberType,
}: UserMenuProps) {
  const router = useRouter();
  const manager = new OmnibusManager();
  const [isOpen, setIsOpen] = useState(false);
  const [notification, setNotification] = useState<{
    type: "error" | "success";
    message: string;
  } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const { impersonationState, loading, startImpersonation, stopImpersonation } =
    useImpersonation();
  const [isViewAsOpen, setIsViewAsOpen] = useState(false);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setIsViewAsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    const { error } = await manager.auth.signOut();

    if (error) {
      setNotification({
        type: "error",
        message: "Logout failed: " + error.message,
      });
    } else {
      window.location.href = "/auth/login";
    }
  };

  const handleChangePassword = () => {
    router.push("/auth/change-password");
  };

  const handleImpersonation = async (
    role: ImpersonationRole,
    memberType?: ImpersonationMemberType
  ) => {
    if (!tenantId || loading) return;
    const success = await startImpersonation(role, tenantId, memberType);
    if (success) {
      // Always navigate to root page to avoid permission issues
      window.location.href = "/?t=" + Date.now();
    }
  };

  return (
    <div className={`relative ${className}`} ref={menuRef}>
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
      >
        <User className="h-4 w-4" />
        <span>{userEmail}</span>
        <ChevronDown
          className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50">
          {memberType !== "portal-admin" && (
            <button
              onClick={() => router.push("/auth/edit-profile")}
              className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <Edit3 className="h-4 w-4" />
              Edit Profile
            </button>
          )}

          <button
            onClick={handleChangePassword}
            className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <Lock className="h-4 w-4" />
            Change Password
          </button>

          {isActuallyAdmin && (
            <div className="relative">
              <div className="border-t border-gray-200 my-1"></div>
              <button
                onClick={() => setIsViewAsOpen((v) => !v)}
                className="flex items-center justify-between w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <span className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  View as…
                </span>
                <ChevronRight
                  className={`h-4 w-4 transition-transform ${isViewAsOpen ? "rotate-90" : ""}`}
                />
              </button>

              {isViewAsOpen && (
                <div className="absolute right-full top-0 mr-2 w-64 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50">
                  <button
                    onClick={() => handleImpersonation("public", "public")}
                    disabled={loading}
                    className="flex items-center justify-between w-full px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-center gap-2">
                      <Eye className="h-4 w-4" />
                      Public
                    </div>
                    {impersonationState.isImpersonating &&
                      impersonationState.impersonatedRole === "public" && (
                        <Check className="h-4 w-4" />
                      )}
                  </button>
                  <button
                    onClick={() => handleImpersonation("member", "owner")}
                    disabled={loading}
                    className="flex items-center justify-between w-full px-4 py-2 text-sm text-green-600 hover:bg-green-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Owner
                    </div>
                    {impersonationState.isImpersonating &&
                      impersonationState.impersonatedRole === "member" &&
                      impersonationState.impersonatedMemberType === "owner" && (
                        <Check className="h-4 w-4" />
                      )}
                  </button>
                  <button
                    onClick={() =>
                      handleImpersonation("member", "board-member")
                    }
                    disabled={loading}
                    className="flex items-center justify-between w-full px-4 py-2 text-sm text-green-700 hover:bg-green-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Board Member
                    </div>
                    {impersonationState.isImpersonating &&
                      impersonationState.impersonatedRole === "member" &&
                      impersonationState.impersonatedMemberType ===
                        "board-member" && <Check className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={() => handleImpersonation("member", "renter")}
                    disabled={loading}
                    className="flex items-center justify-between w-full px-4 py-2 text-sm text-green-700 hover:bg-green-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Renter
                    </div>
                    {impersonationState.isImpersonating &&
                      impersonationState.impersonatedRole === "member" &&
                      impersonationState.impersonatedMemberType ===
                        "renter" && <Check className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={() =>
                      handleImpersonation("member", "property-manager")
                    }
                    disabled={loading}
                    className="flex items-center justify-between w-full px-4 py-2 text-sm text-green-700 hover:bg-green-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Property Manager
                    </div>
                    {impersonationState.isImpersonating &&
                      impersonationState.impersonatedRole === "member" &&
                      impersonationState.impersonatedMemberType ===
                        "property-manager" && <Check className="h-4 w-4" />}
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="border-t border-gray-200 my-1"></div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Logout
          </button>
        </div>
      )}
    </div>
  );
}
